import React, { useRef, useState, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { Download, Share2, Sparkles } from 'lucide-react';

const GOLD = '#fbbf24';
const GOLD_DARK = '#b45309';

const TicketStyles = () => (
  <style>{`
    @keyframes cdTicketIn { 0% { opacity:0; transform: translateY(40px) scale(.9) rotate(-3deg); } 70% { opacity:1; transform: translateY(-6px) scale(1.02) rotate(.5deg); } 100% { transform: none; } }
    @keyframes cdShine { 0% { transform: translateX(-120%) skewX(-20deg); } 100% { transform: translateX(260%) skewX(-20deg); } }
    @keyframes cdConfetti { 0% { opacity:1; transform: translate(0,0) rotate(0); } 100% { opacity:0; transform: translate(var(--dx), 420px) rotate(var(--rot)); } }
    .cd-ticket-in { animation: cdTicketIn .9s cubic-bezier(.2,.9,.3,1.2) both; }
    .cd-shine { animation: cdShine 1.6s ease-out .7s both; }
  `}</style>
);

function ConfettiBurst() {
  const pieces = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    left: `${(i * 2.8) % 100}%`,
    dx: `${((i % 7) - 3) * 22}px`,
    rot: `${(i % 2 ? 1 : -1) * (180 + i * 20)}deg`,
    delay: `${(i % 9) * 0.07}s`,
    color: ['#fbbf24', '#fde68a', '#f59e0b', '#ffffff', '#fca5a5'][i % 5],
    w: 5 + (i % 3) * 2,
  })), []);
  return (
    <div aria-hidden data-html2canvas-ignore className="pointer-events-none absolute inset-x-0 -top-4 h-0 z-20">
      {pieces.map((p, i) => (
        <span key={i} style={{
          position: 'absolute', left: p.left, top: 0, width: p.w, height: p.w * 1.6, background: p.color, borderRadius: 2,
          '--dx': p.dx, '--rot': p.rot, animation: `cdConfetti 2.4s ease-in ${p.delay} both`,
        }} />
      ))}
    </div>
  );
}

const Corner = ({ pos }) => (
  <span aria-hidden style={{
    position: 'absolute', width: 22, height: 22, borderColor: GOLD, borderStyle: 'solid', borderWidth: 0,
    ...(pos.includes('t') ? { top: 10, borderTopWidth: 2 } : { bottom: 10, borderBottomWidth: 2 }),
    ...(pos.includes('l') ? { left: 10, borderLeftWidth: 2 } : { right: 10, borderRightWidth: 2 }),
  }} />
);

const Row = ({ label, value, testid }) => (
  <div>
    <p className="font-manrope font-700 uppercase text-[9px] tracking-[0.3em]" style={{ color: 'rgba(251,191,36,0.75)' }}>{label}</p>
    <p className="font-playfair font-bold text-white text-base leading-tight mt-0.5" data-testid={testid}>{value}</p>
  </div>
);

function TicketCard({ booking, pkg, event }) {
  const isCena = pkg.type === 'capodanno_cena';
  const included = (pkg.included || []).map(s => (s || '').trim()).filter(Boolean);
  const total = (pkg.price || 0) * (booking.num_people || 1);
  return (
    <div className="relative rounded-[22px] overflow-hidden" style={{
      background: 'linear-gradient(160deg, #2a1708 0%, #140819 55%, #0a0514 100%)',
      border: `2px solid ${GOLD}`, boxShadow: '0 0 0 5px #0a0514, 0 0 0 6px rgba(251,191,36,0.45)',
    }}>
      <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
      <div className="px-7 pt-8 pb-6 text-center">
        <p className="font-manrope font-800 uppercase tracking-[0.45em] text-[10px]" style={{ color: GOLD }}>La Peschiera Club presenta</p>
        <h3 className="font-playfair font-900 text-white leading-[1.05] mt-3" style={{ fontSize: '1.65rem' }}>Gran Veglione<br />di Capodanno</h3>
        <p className="font-playfair italic mt-1" style={{ color: '#fde68a', fontSize: '1.05rem' }}>con Franco Bagutti</p>
        <div className="inline-flex items-center gap-2 mt-4 px-4 py-1.5 rounded-full font-manrope font-800 uppercase tracking-[0.3em] text-[11px]"
          data-testid="ticket-type-label"
          style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_DARK})`, color: '#1a0a0a' }}>
          {isCena ? 'Cena di Gala' : 'Serata'}
        </div>
        <p className="font-playfair font-900 mt-4 tracking-[0.15em]" style={{ color: GOLD, fontSize: '1.9rem' }}>31 · 12 · 2026</p>
        <p className="font-manrope text-gray-400 text-xs mt-1">{isCena ? `Ore ${event?.time || '20:00'}` : 'Dalle ore 21:30'} · Valdengo (BI)</p>
      </div>

      <div className="px-7 pb-5 grid grid-cols-2 gap-4 text-left">
        <Row label="Intestato a" value={`${booking.first_name} ${booking.last_name}`} testid="ticket-name" />
        <Row label="Persone" value={booking.num_people} testid="ticket-people" />
        <Row label="Pacchetto" value={pkg.label} testid="ticket-package" />
        <Row label="Totale" value={`€ ${total}`} testid="ticket-total" />
      </div>
      {included.length > 0 && (
        <div className="px-7 pb-5">
          <p className="font-manrope font-700 uppercase text-[9px] tracking-[0.3em] mb-1.5" style={{ color: 'rgba(251,191,36,0.75)' }}>Compreso</p>
          <p className="font-manrope text-white text-sm" data-testid="ticket-included">{included.join(' · ')}</p>
        </div>
      )}

      {/* Perforation */}
      <div className="relative flex items-center" aria-hidden>
        <span className="absolute -left-3 w-6 h-6 rounded-full" style={{ background: '#0a0514', border: `2px solid ${GOLD}` }} />
        <div className="flex-1 mx-5" style={{ borderTop: `2px dashed rgba(251,191,36,0.5)` }} />
        <span className="absolute -right-3 w-6 h-6 rounded-full" style={{ background: '#0a0514', border: `2px solid ${GOLD}` }} />
      </div>

      <div className="px-7 py-6 text-center">
        <p className="font-manrope font-700 uppercase text-[10px] tracking-[0.35em]" style={{ color: 'rgba(251,191,36,0.75)' }}>Numero ticket</p>
        <p className="font-manrope font-800 tracking-[0.14em] whitespace-nowrap mt-1" style={{ color: GOLD, fontSize: '1.7rem', fontVariantNumeric: 'lining-nums tabular-nums' }} data-testid="ticket-code">{booking.booking_code}</p>
        <p className="font-manrope text-gray-400 text-[11px] mt-1 leading-snug">Mostra questo biglietto all'ingresso</p>
      </div>
    </div>
  );
}

export function CapodannoTicket({ booking, pkg, event, onClose }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const filename = `capodanno-${booking.booking_code}.png`;

  const toBlob = async () => {
    const canvas = await html2canvas(ref.current, { backgroundColor: '#0a0514', scale: 2, useCORS: true });
    return new Promise(res => canvas.toBlob(res, 'image/png'));
  };

  const download = async () => {
    setBusy(true);
    try {
      const blob = await toBlob();
      const link = document.createElement('a');
      link.download = filename;
      link.href = URL.createObjectURL(blob);
      link.click();
    } finally { setBusy(false); }
  };

  const share = async () => {
    const text = `Il mio biglietto per il Gran Veglione di Capodanno con Franco Bagutti — La Peschiera Club. Codice: ${booking.booking_code}`;
    setBusy(true);
    try {
      const blob = await toBlob();
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
        return;
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } catch (e) {
      if (e?.name !== 'AbortError') window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 sm:p-6 relative" data-testid="capodanno-booking-success">
      <TicketStyles />
      <ConfettiBurst />
      <p className="text-center font-playfair font-bold text-white text-xl mb-4 flex items-center justify-center gap-2">
        <Sparkles size={18} style={{ color: GOLD }} /> Ci vediamo a Capodanno!
      </p>
      <div className="relative cd-ticket-in" data-testid="capodanno-ticket">
        <div ref={ref} style={{ padding: 6, background: '#0a0514', borderRadius: 26 }}>
          <TicketCard booking={booking} pkg={pkg} event={event} />
        </div>
        <div aria-hidden data-html2canvas-ignore className="pointer-events-none absolute inset-0 overflow-hidden rounded-[26px]">
          <div className="cd-shine absolute inset-y-0 w-1/3" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,240,200,0.35), transparent)' }} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-5">
        <button onClick={download} disabled={busy} data-testid="ticket-download-btn"
          className="inline-flex items-center justify-center gap-2 py-3 rounded-full font-manrope font-800 uppercase tracking-wider text-xs transition-transform hover:-translate-y-0.5"
          style={{ background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`, color: '#1a0a0a', opacity: busy ? 0.6 : 1 }}>
          <Download size={15} /> Scarica
        </button>
        <button onClick={share} disabled={busy} data-testid="ticket-share-whatsapp-btn"
          className="inline-flex items-center justify-center gap-2 py-3 rounded-full font-manrope font-800 uppercase tracking-wider text-xs transition-transform hover:-translate-y-0.5"
          style={{ background: 'rgba(37,211,102,0.15)', border: '1px solid rgba(37,211,102,0.5)', color: '#4ade80', opacity: busy ? 0.6 : 1 }}>
          <Share2 size={15} /> WhatsApp
        </button>
      </div>
      <button onClick={onClose} data-testid="ticket-close-btn"
        className="mt-3 w-full py-2.5 rounded-full font-manrope font-700 uppercase tracking-widest text-xs text-gray-400 hover:text-white"
        style={{ border: '1px solid rgba(255,255,255,0.12)' }}>Chiudi</button>
    </div>
  );
}
